<template>
  <div class="advanced-table-wrapper">
    <div class="table-area">
      <div :class="['actions-row', { 'justify-end': !showExcelButton && !isFirstJoin }]">
        <div v-if="isFirstJoin && products.length > 0" class="first-join-message">
          <p>Aşağıda yalnızca satışa açık, stoğu bulunan ve diğer komisyon tekliflerine dahil edilmemiş  ürünleriniz listelenmektedir. Bu kampanyaya dahil olmasını istediğiniz ürünleri listeden seçin.</p>
        </div>
        <div v-else-if="products.length > 0 && showExcelButton" class="excel-button-wrapper">
          <AppButton
            secondary
            sm
            title="Excel ile İndir"
            @buttonClick="handleExportExcel"
          />
        </div>
        <div v-if="products.length > 0" class="pagination-wrapper">
          <Pagination
            :page-count="totalPages"
            :selected-page-index="currentPage"
            :page-range="5"
            :show-per-page="true"
            :per-page-items="{
              10: '10 Ürün',
              25: '25 Ürün',
              50: '50 Ürün'
            }"
            :selected-per-page-key="pageSize"
            :per-page-menu-position="'top-left'"
            @change-index="handlePageChange"
            @change-per-page="handlePageSizeChange"
          />
        </div>
      </div>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr>
              <th>
                <Checkbox
                  id="select-all-products"
                  :checked="isAllSelected"
                  :indeterminate="isSomeSelected"
                  :onClickFn="handleSelectAll"
                />
              </th>
              <th>Ürün Adı</th>
              <th>Stok (Adet)</th>
              <th>Stok Kodu</th>
              <th>Kampanya Komisyon Oranı</th>
              <th>Güncel Fiyat</th>
              <th>Önerilen En Yüksek Fiyat</th>
              <th>
                <TableHeadTooltip
                  v-if="showActionsColumn"
                  title="n11 Satış Fiyatı"
                  tooltipText="Güncel fiyat, önerilen en yüksek fiyatın altında olduğu için yeni bir fiyat girmenize gerek yoktur. Ürün mevcut fiyatla satışta kalmaya devam edecektir."
                />
                <span v-else>n11 Satış Fiyatı</span>
              </th>
            </tr> 
          </thead>
          <tbody>
            <tr v-if="isLoading" class="loading-row">
              <td :colspan="8">
                <div class="loading">
                  <img src="@/assets/images/loading.gif" alt="Loading" />
                  <span class="loading-text">Yükleniyor</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="products.length === 0" class="empty-row">
              <td :colspan="8">
                <div class="empty-table">
                  <img src="@/assets/images/empty-search.png" alt="Ürün bulunamadı" />
                  <span class="title">Kampanyada ürün bulunmuyor.</span>
                </div>
              </td>
            </tr>
            <tr 
              v-else 
              v-for="product in products" 
              :key="product.id"
              :class="{ 'selected-row': selectedProducts.includes(product.id) }"
            >
              <td>
                <Checkbox
                  :id="`product-${product.id}`"
                  :checked="selectedProducts.includes(product.id)"
                  :onClickFn="() => handleToggleProduct(product.id)"
                />
              </td>
              <td>
                <div class="product-name">
                  <div class="product-title">
                    <img 
                      width="64" 
                      height="64" 
                      :src="product.image || defaultProductImage" 
                      @error="handleImageError"
                      alt="Ürün Resmi" 
                    />
                    <div class="product-name-content">
                      <span class="product-name-text">{{ product.name }}</span>
                      <TooltipPlugin
                        v-if="showAnotherCampaignBadge && product.includedInAnotherCampaign"
                        position="bottom"
                        :tooltip-width="230"
                        :tooltip-text="ANOTHER_CAMPAIGN_BADGE_TOOLTIP"
                      >
                        <template #tooltip-btn>
                          <span class="another-campaign-badge">
                            BAŞKA KAMPANYAYA DAHİL
                            <Icons name="InfoCircle" color="#4B7BE5" size="xs" />
                          </span>
                        </template>
                      </TooltipPlugin>
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div class="product-stock">{{ product.stock ?? '-' }}</div>
              </td>
              <td>
                <div class="product-stock-code">{{ product.stockCode }}</div>
              </td>
              <td>
                <div class="product-commission-rate">%{{ product.commissionRate }}</div>
              </td>
              <td>
                <div class="product-current-price">{{ formatDisplayPrice(product.currentPrice) }}</div>
              </td>
              <td>
                <div class="product-suggested-max-price">{{ formatDisplayPrice(product.suggestedMaxPrice) }}</div>
              </td>
              <td>
                <PopoverInput
                  v-if="showActionsColumn"
                  :ref="(el) => { if (el) popoverInputRefs[product.id] = el }"
                  :value="product.price ?? null"
                  text="Piyasa Satış Fiyatı"
                  description="Gireceğiniz fiyat, n11 Satış Fiyatı olarak belirlenecektir."
                  :inputId="`product-price-${product.id}`"
                  :popoverStyle="{ padding: '0px' }"
                  :isValid="validatePrice(product.id, product.price)"
                  :validate="(value) => validatePrice(product.id, value)"
                  :format="formatPrice"
                  :isPrice="true"
                  :isDisabled="product.isDisabled === true"
                  :isActionClick="false"
                  :getErrorMessageFn="(value) => getErrorMessage(product.id, value)"
                  @input="(value) => handlePriceInput(product.id, value)"
                  @update="(value, revert) => handlePriceUpdate(product.id, value, revert)"
                />
                <div v-else class="product-n11-sale-price">{{ formatDisplayPrice(product.n11SalePrice) }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!isLoading && products.length > 0" class="pagination bottom-pagination">
        <Pagination
          :page-count="totalPages"
          :selected-page-index="currentPage"
          :page-range="5"
          :show-per-page="true"
          :per-page-items="{
            10: '10 Ürün',
            25: '25 Ürün',
            50: '50 Ürün'
          }"
          :selected-per-page-key="pageSize"
          :per-page-menu-position="'top-left'"
          @change-index="handlePageChange"
          @change-per-page="handlePageSizeChange"
        />
      </div>
    </div>

    <!-- Export Excel Modal -->
    <Modal
      ref="exportModalRef"
      :show-icon="false"
      positive-action-text="Devam Et"
      @on-positive-action-click="handleContinue"
      @modal-closed="handleCloseModal"
    >
      <template #body>
        <div class="modal-body-content">
          <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
          <p>Kampanyaya toplu ürün eklemek için şablon indirme adımına yönlendirileceksiniz.</p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Icons, Pagination, AppButton, Checkbox, Modal, TooltipPlugin } from '@n11/so-ui-components';
import TableHeadTooltip from '@/components/DataTable/TableHeadTooltip.vue';
import PopoverInput from '@/components/DataTable/PopoverInput.vue';
import defaultProductImage from '@/assets/images/icons/default-product-image.svg';

const ANOTHER_CAMPAIGN_BADGE_TOOLTIP =
  'Bu ürün başka bir sepette indirim kampanyasında yer alıyor. <br/> Yeni bir kampanyaya eklerseniz mevcut kampanyadan çıkarılır.';

const handleImageError = (event) => {
  event.target.src = defaultProductImage;
};

const formatDisplayPrice = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return value;
  const formatted = num.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
  return formatted.includes(',00') ? formatted.replace(',00', '') : formatted;
};

const props = defineProps({
  products: {
    type: Array,
    default: () => [],
  },
  currentPage: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showExcelButton: {
    type: Boolean,
    default: false,
  },
  isFirstJoin: {
    type: Boolean,
    default: false,
  },
  showActionsColumn: {
    type: Boolean,
    default: true,
  },
  showAnotherCampaignBadge: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['page-change', 'page-size-change', 'export-excel', 'selection-change', 'price-change']);

const route = useRoute();
const router = useRouter();

const selectedProducts = ref([]);
const popoverInputRefs = ref({});

const isAllSelected = computed(() => {
  return props.products.length > 0 && props.products.every(p => selectedProducts.value.includes(p.id));
});

const isSomeSelected = computed(() => {
  const currentPageSelectedCount = props.products.filter(p => selectedProducts.value.includes(p.id)).length;
  return currentPageSelectedCount > 0 && currentPageSelectedCount < props.products.length;
});

const handleSelectAll = () => {
  const currentPageIds = props.products.map(p => p.id);
  if (isAllSelected.value) {
    selectedProducts.value = selectedProducts.value.filter(id => !currentPageIds.includes(id));
  } else {
    const newSelections = currentPageIds.filter(id => !selectedProducts.value.includes(id));
    selectedProducts.value = [...selectedProducts.value, ...newSelections];
  }
  emit('selection-change', selectedProducts.value);
};

const handleToggleProduct = (productId) => {
  const index = selectedProducts.value.indexOf(productId);
  if (index > -1) {
    selectedProducts.value.splice(index, 1);
  } else {
    selectedProducts.value.push(productId);
  }
  emit('selection-change', selectedProducts.value);
};

const handlePageChange = (pageIndex) => {
  emit('page-change', pageIndex);
};

const handlePageSizeChange = (newPageSize) => {
  emit('page-size-change', Number(newPageSize));
};

const exportModalRef = ref(null);

const handleExportExcel = () => {
  if (exportModalRef.value) {
    exportModalRef.value.openModal();
  }
};

const handleCloseModal = () => {
  if (exportModalRef.value) {
    exportModalRef.value.hiddenModal();
  }
};

const handleContinue = () => {
  const campaignId = route.params.campaignId || '152';
  router.push({
    name: 'JoinCampaignBulk',
    params: { campaignId }
  });
  handleCloseModal();
};

const formatPrice = (price) => {
  if (!price || price === '' || price === null || price === undefined) return '';
  const numValue = toNumericPrice(price);
  if (isNaN(numValue) || !isFinite(numValue)) return '';
  const formattedPrice = numValue.toLocaleString("tr-TR", { style: 'currency', currency: 'TRY' });
  return formattedPrice.includes(',00') ? formattedPrice.replace(',00', '') : formattedPrice;
};

const toNumericPrice = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined || value === '') return NaN;

  const normalizedValue = String(value)
    .replace(/\./g, '')
    .replace(',', '.');

  return Number(normalizedValue);
};

const handlePriceUpdate = (productId, value, revert) => {
  const product = props.products.find(p => p.id === productId);
  if (!product) {
    console.log('[Umbrella][PriceUpdate] Product not found', { productId, value });
    return;
  }

  if (!validatePrice(productId, value)) {
    console.log('[Umbrella][PriceUpdate] Validation failed', {
      productId,
      productStockCode: product.id,
      rawValue: value,
      parsedValue: toNumericPrice(value),
      maxPrice: product.maxPrice,
    });
    if (revert) revert();
    return;
  }

  console.log('[Umbrella][PriceUpdate] Before emit', {
    productId,
    productStockCode: product.id,
    rawValue: value,
    parsedValue: toNumericPrice(value),
    productPriceBefore: product.price,
    maxPrice: product.maxPrice,
  });

  product.price = value;
  const payload = {
    id: product.id,
    productId: product.productId || product.id,
    minPrice: value,
    maxPrice: product.maxPrice || 0,
  };

  emit('price-change', payload);

  const popoverInput = popoverInputRefs.value[productId];
  if (popoverInput?.$refs?.popoverRef) {
    popoverInput.$refs.popoverRef.popoverToogle();
  }
};

const handlePriceInput = (productId, value) => {
  const product = props.products.find(p => p.id === productId);
  if (!product) {
    return;
  }

  if (value === null || value === undefined || value === '') {
    return;
  }

  const payload = {
    id: product.id,
    productId: product.productId || product.id,
    minPrice: value,
    maxPrice: product.maxPrice || 0,
  };

  emit('price-change', payload);
};

const validatePrice = (productId, value) => {
  const product = props.products.find(p => p.id === productId);
  if (!product) return true;
  
  const numericValue = toNumericPrice(value);
  const maxPrice = product.maxPrice || 0;
  
  if (value === null || value === undefined || value === '' || isNaN(numericValue)) {
    return true;
  }
  
  if (numericValue === 0) {
    return false;
  }
  
  if (numericValue > maxPrice) {
    return false;
  }
  
  return true;
};

const getErrorMessage = (productId, value) => {
  const product = props.products.find(p => p.id === productId);
  if (!product) return '';
  
  const numericValue = toNumericPrice(value);
  
  if (numericValue === 0 && value !== null && value !== undefined && value !== '') {
    return 'Lütfen fiyat giriniz.';
  }
  
  const maxPrice = product.maxPrice || 0;
  if (numericValue > maxPrice) {
    return 'Girdiğiniz satış fiyatı, önerilen kampanyalı tutardan yüksek olamaz.';
  }
  
  return 'Girdiğiniz satış fiyatı, önerilen kampanyalı tutardan yüksek olamaz.';
};

// Products değiştiğinde sadece mevcut sayfadaki seçimleri güncelle
// (Sayfa değiştiğinde seçimleri korumak için watch kaldırıldı)
// Filtre değiştiğinde parent component'te seçimler temizlenecek
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';


.advanced-table-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
  margin-bottom: 100px;
  width: 100%;
  background: $colorN00;
  border-radius: 8px;
  padding: 24px;

  :deep(.first-join-message) {
    padding: 0px !important;
  }
  
  .table-area {
    position: relative;
    z-index: 0;
    overflow: visible;

    .actions-row {
      display: flex;
      width: 100%;
      justify-content: space-between;
      margin-bottom: 16px;

      &.justify-end {
        justify-content: flex-end !important;
      }

      .excel-button-wrapper {
        display: flex;
        align-items: center;
      }

      .first-join-message {
        width: 738px;
        padding: 16px 16px 16px 0;
        color: $colorN80;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: -0.05px;

        p {
          margin: 0;
          color: $colorN80;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: -0.05px;
        }
      }
    }

    .table-wrapper {
      position: relative;
      overflow: visible;
      width: 100%;
      max-width: 1328px;
      border: 1px solid $colorN50;
      border-radius: 8px;
      border-collapse: separate;
      border-spacing: 0;

      .loading-row,
      .empty-row {
        td {
          padding: 0 !important;
          border: none !important;
        }
      }

      .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 64px 0;
        min-height: 400px;

        img {
          max-width: 120px;
          margin-bottom: 16px;
        }

        .loading-text {
          display: block;
          color: $colorN80;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          text-align: center;
        }
      }

      .empty-table {
        position: sticky;
        left: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 64px 0;
        min-height: 400px;

        img {
          max-width: 120px;
          margin-bottom: 24px;
        }

        .title {
          display: block;
          color: $colorN80;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          text-align: center;
        }
      }

      .advanced-table {
        width: 100%;
        border-radius: 8px;
        border-collapse: separate;

        thead {
          position: relative;
          z-index: 200;
          background: $colorD10;
          color: $colorN80;

          tr {
            height: 50px;

            th {
              position: sticky !important;
              z-index: 200 !important;
              top: 0;
              padding: 14px 8px 14px 12px;
              border-bottom: 2px solid $colorN30;
              background-color: $colorBe30;
              color: $colorN80;
              font-size: 13px;
              font-weight: 600;
              letter-spacing: -0.05px;
              line-height: 18px;
              text-align: left;
              vertical-align: middle;

              &:not(:last-child) {
                border-right: 1px solid $colorN30;
              }
            }
          }
        }

        tbody {
          position: relative;

          tr {
            &:not(:last-child) td {
              border-bottom: 2px solid $colorN30;
            }

            &.selected-row {
              td {
                background: #FFEFFD;
                border-bottom: 2px solid #D9D9D9;
              }
            }

            td {
              height: 90px;
              background-color: $colorN00;
              vertical-align: middle;
              padding: 14px 8px 14px 12px;
              color: $colorN80;
              font-size: 13px;
              letter-spacing: -0.05px;
              line-height: 18px;
              position: relative;
              overflow: visible;

              &:not(:last-child) {
                border-right: 1px solid $colorN30;
              }

              .product-name {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .product-title {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  color: $colorN80;
                  font-size: 13px;
                  font-weight: 400;
                  line-height: 18px;
                  letter-spacing: -0.05px;

                  img {
                    flex-shrink: 0;
                    border-radius: 4px;
                    object-fit: cover;
                  }

                  .product-name-content {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                  }

                  .product-name-text {
                    max-width: 176px;
                    max-height: 32px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                    line-clamp: 2;
                    line-height: 16px;
                  }

                  .another-campaign-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 2px;
                    padding: 2px 4px;
                    border-radius: 4px;
                    background: $colorB05;
                    color: $colorB60;
                    font-size: 10px;
                    font-weight: 600;
                    line-height: 14px;
                    width: fit-content;
                    height: 20px;

                    :deep(.icon) {
                      width: 13px;
                      height: 13px;
                      display: flex;
                      align-items: center;

                      svg {
                        width: 13px !important;
                        height: 13px !important;
                      }
                    }
                  }
                }
              }

              .product-stock,
              .product-stock-code,
              .product-commission-rate,
              .product-current-price,
              .product-suggested-max-price,
              .product-n11-sale-price {
                color: $colorN80;
                font-size: 13px;
                font-weight: 400;
                line-height: 18px;
                letter-spacing: -0.05px;
              }
            }
          }
        }
      }

      :deep(.popover-wrapper) {
        position: relative;
        z-index: auto !important;
      }

      :deep(.popover),
      :deep(.popover-content),
      :deep(.popover__content) {
        z-index: auto !important;
      }

      :deep(.popover-text),
      :deep(.popover-wrapper__content) {
        z-index: 10012 !important;
      }

      :deep(.popover-text.top-center) {
        left: auto !important;
        right: -40px !important;
        transform: translate(0, -100%) !important;
      }

      :deep(.popover-wrapper__content) {
        transform: none !important;
      }
    }

    .pagination-wrapper {
      position: relative;
      z-index: 10010;

      :deep(.simpleSelect-btn.simpleSelect-btn) {
        width: 100% !important;
      }

      :deep(.simpleSelect-content),
      :deep(.simpleSelect-list),
      :deep(.simpleSelect-options) {
        z-index: 10011 !important;
      }
    }
    .pagination {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: flex-end;
      gap: 24px;
      margin-top: 24px;
      position: relative;
      z-index: 10010;

      :deep(.simpleSelect-content),
      :deep(.simpleSelect-list),
      :deep(.simpleSelect-options) {
        z-index: 10011 !important;
      }
    }
  }
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  :deep(.icon) {
    width: 60px;
    height: 60px;

    svg {
      width: 60px !important;
      height: 60px !important;
    }
  }

  p {
    margin: 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.02px;
    text-align: center;
  }
}
</style>
